/**
 * @file legalAcceptanceService.js
 * @description Versioned acceptance ledger for Terms of Service and Privacy
 *   Policy. Mirrors the existing baa_acceptances pattern. Records which
 *   version a user accepted, when, and from where — required to enforce
 *   v1.0 (and future versions) in any contested dispute, and to gate the
 *   app on having a current acceptance row.
 *
 *   Usage:
 *     // On signup: stamp both
 *     await recordLegalAcceptance(userId, "tos", TOS_VERSION);
 *     await recordLegalAcceptance(userId, "pp",  PP_VERSION);
 *
 *     // On every login: check whether the current version is accepted
 *     const status = await getAcceptanceStatus(userId);
 *     if (!status.tosCurrent || !status.ppCurrent) {
 *       // show <AcceptanceGate> blocking modal
 *     }
 *
 * @author Victor Carpinelli
 * @version 1.0.0
 * @module lib/legalAcceptanceService
 */
import { supabase } from "./supabase";
import { TOS_VERSION, PP_VERSION, legalDocHash } from "./legalText";

const TABLES = {
  tos: "tos_acceptances",
  pp:  "pp_acceptances",
};

const CURRENT_VERSIONS = {
  tos: TOS_VERSION,
  pp:  PP_VERSION,
};

/**
 * @description Best-effort client IP lookup for the e-signature audit trail,
 *   matching the signup path. Fails silently (returns null) — an acceptance is
 *   still recorded without it.
 * @returns {Promise<string|null>}
 */
async function lookupClientIp() {
  try {
    const res = await fetch("https://api.ipify.org?format=json", { signal: AbortSignal.timeout(3000) });
    if (res.ok) { const d = await res.json(); return d.ip || null; }
  } catch { /* IP lookup failed — acceptance is still recorded */ }
  return null;
}

/**
 * @description Records an acceptance row for the given document type at the
 *   currently-deployed version. Captures version, user-agent, and client IP so
 *   the re-acceptance audit trail matches the signup path. (The DB stamps
 *   accepted_at server-side via the column default — not client-supplied.)
 *
 * @param {string} userId
 * @param {"tos" | "pp"} docType
 * @param {string} [version] - Defaults to the current bundled version
 * @param {string|null} [ipAddress] - Pass a pre-fetched IP to avoid a second
 *   lookup; omit entirely to have this call look it up itself.
 * @returns {Promise<{ok: boolean, error?: string}>}
 */
export async function recordLegalAcceptance(userId, docType, version, ipAddress = undefined) {
  if (!userId)                return { ok: false, error: "missing_user_id" };
  if (!TABLES[docType])       return { ok: false, error: "invalid_doc_type" };

  const v = version || CURRENT_VERSIONS[docType];
  const userAgent = typeof navigator !== "undefined" ? navigator.userAgent : null;
  const ip = ipAddress !== undefined ? ipAddress : await lookupClientIp();
  // Tamper-evident hash of the exact text accepted (null-safe if no WebCrypto).
  const docHash = await legalDocHash(docType);

  const { error } = await supabase
    .from(TABLES[docType])
    .insert({ user_id: userId, version: v, user_agent: userAgent, ip_address: ip, doc_hash: docHash });

  if (error) return { ok: false, error: error.message };
  return { ok: true };
}

/**
 * @description Returns the most recent acceptance row for a user + doc type,
 *   or null if none exists.
 * @param {string} userId
 * @param {"tos" | "pp"} docType
 * @returns {Promise<{version: string, accepted_at: string} | null>}
 */
export async function getLatestAcceptance(userId, docType) {
  if (!userId || !TABLES[docType]) return null;
  const { data, error } = await supabase
    .from(TABLES[docType])
    .select("version, accepted_at")
    .eq("user_id", userId)
    .order("accepted_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error || !data) return null;
  return data;
}

/**
 * @description Single-query check covering both ToS and Privacy Policy.
 *   Returns whether the user has accepted the current version of each.
 *   Used by App.js to decide whether to show the AcceptanceGate.
 *
 * @param {string} userId
 * @returns {Promise<{ tosCurrent: boolean, ppCurrent: boolean,
 *                     tosVersion: string|null, ppVersion: string|null,
 *                     loading: false }>}
 */
// Session-scoped cache. Acceptance status cannot change within a session
// except when the user accepts via the gate — recordBothAcceptances()
// invalidates this so the next check reflects the new state. Keyed by the
// current ToS/PP versions so a version bump auto-busts the cache.
const _statusCacheKey = (userId) => `legal:status:${userId}:${TOS_VERSION}:${PP_VERSION}`;

export function invalidateAcceptanceStatus(userId) {
  try { sessionStorage.removeItem(_statusCacheKey(userId)); } catch { /* ignore */ }
}

export async function getAcceptanceStatus(userId) {
  if (!userId) {
    return { tosCurrent: false, ppCurrent: false, tosVersion: null, ppVersion: null };
  }
  // Return cached result if present — avoids re-querying tos/pp tables on
  // every profile-state change. Only "current=true" results are cached;
  // a not-yet-accepted state stays uncached so the gate re-checks until met.
  try {
    const cached = sessionStorage.getItem(_statusCacheKey(userId));
    if (cached) return JSON.parse(cached);
  } catch { /* sessionStorage unavailable or corrupt cache entry — fall through to a fresh query below */ }

  const [tos, pp] = await Promise.all([
    getLatestAcceptance(userId, "tos"),
    getLatestAcceptance(userId, "pp"),
  ]);
  const result = {
    tosCurrent: tos?.version === TOS_VERSION,
    ppCurrent:  pp?.version  === PP_VERSION,
    tosVersion: tos?.version || null,
    ppVersion:  pp?.version  || null,
  };
  // Cache only when both are current — the steady state for the whole
  // session. An incomplete state is left uncached so the gate keeps checking.
  if (result.tosCurrent && result.ppCurrent) {
    try { sessionStorage.setItem(_statusCacheKey(userId), JSON.stringify(result)); } catch { /* ignore */ }
  }
  return result;
}

/**
 * @description Records both ToS and PP acceptance in one atomic batch.
 *   Used by the AcceptanceGate when the user clicks "I accept both."
 * @param {string} userId
 * @returns {Promise<{ok: boolean, error?: string}>}
 */
export async function recordBothAcceptances(userId) {
  // One IP lookup shared by both inserts — consistent e-signature audit trail.
  const ip = await lookupClientIp();
  const [tos, pp] = await Promise.all([
    recordLegalAcceptance(userId, "tos", undefined, ip),
    recordLegalAcceptance(userId, "pp",  undefined, ip),
  ]);
  if (!tos.ok) return tos;
  if (!pp.ok)  return pp;
  // Bust the cached status so the next getAcceptanceStatus() reflects the
  // freshly-recorded acceptance instead of a stale "not accepted" miss.
  invalidateAcceptanceStatus(userId);
  return { ok: true };
}
